import { CampaignCreateParams, Provider, Subscription } from '../../../types'
import { UseFormReturn, useWatch, useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useContext } from 'react'
import { ProjectContext } from '../../../contexts'
import { EmailIcon, TextIcon, PushIcon, WebhookIcon, InAppIcon } from '../../../ui/icons'
import { Alert } from '../../../ui'
import { LinkButton } from '../../../ui/Button'
import TileGrid, { TileOption } from './TileGrid'

interface ChannelTilesProps {
    subscriptions: Subscription[]
    providers: Provider[]
    form: UseFormReturn<CampaignCreateParams>
}

interface ChannelConfigItem {
    labelKey: string
    icon: React.ReactNode
}

const channelConfig: Record<string, ChannelConfigItem> = {
    email: { labelKey: 'wizard_channel_email', icon: <EmailIcon /> },
    text: { labelKey: 'wizard_channel_text', icon: <TextIcon /> },
    push: { labelKey: 'wizard_channel_push', icon: <PushIcon /> },
    webhook: { labelKey: 'wizard_channel_webhook', icon: <WebhookIcon /> },
    in_app: { labelKey: 'wizard_channel_in_app', icon: <InAppIcon /> },
}

export default function CampaignChannelTiles({ subscriptions, providers, form }: ChannelTilesProps) {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const providerGroups = new Set(providers.map(p => p.group))
    const channels = [...new Set(subscriptions.map(item => item.channel))]
        .filter(channel => providerGroups.has(channel === 'in_app' ? 'push' : channel))

    const value = useWatch({
        control: form.control,
        name: 'channel',
    })

    const { field: { onChange } } = useController({
        control: form.control,
        name: 'channel',
        rules: { required: true },
    })

    const options: TileOption[] = channels
        .filter(channel => channelConfig[channel])
        .map(channel => ({
            value: channel,
            content: (
                <>
                    <div className="tile-icon">{channelConfig[channel].icon}</div>
                    <span className="tile-label">{t(channelConfig[channel].labelKey)}</span>
                </>
            ),
        }))

    if (options.length === 0) {
        return (
            <Alert
                variant="plain"
                title={t('wizard_no_channels_title')}
                style={{ marginTop: 20 }}
                actions={
                    <LinkButton to={`/projects/${project.id}/settings/integrations`}>
                        {t('setup_integration')}
                    </LinkButton>
                }
            >
                {t('wizard_no_channels_desc')}
            </Alert>
        )
    }

    return (
        <TileGrid
            label={t('wizard_channel_label')}
            required
            columns={options.length}
            value={value}
            onChange={onChange}
            options={options}
            className="tile-grid-channel"
        />
    )
}
